// Device fingerprinting utility
// Generates a unique device identifier based on browser and device characteristics

export const generateDeviceFingerprint = () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('Device fingerprint', 2, 2);
  
  const fingerprint = {
    // Screen properties
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    screenColorDepth: window.screen.colorDepth,
    screenPixelDepth: window.screen.pixelDepth,
    
    // Browser properties
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages?.join(','),
    platform: navigator.platform,
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    
    // Hardware properties
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: navigator.deviceMemory,
    maxTouchPoints: navigator.maxTouchPoints,
    
    // Canvas fingerprint
    canvasFingerprint: canvas.toDataURL(),
    
    // Timezone
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    
    // Additional browser features
    webglVendor: getWebGLVendor(),
    webglRenderer: getWebGLRenderer(),
    
    // Generate hash from all properties
    timestamp: Date.now()
  };
  
  // Create a hash from the fingerprint
  const fingerprintString = JSON.stringify(fingerprint);
  const hash = simpleHash(fingerprintString);
  
  return {
    fingerprint: fingerprint,
    hash: hash,
    deviceId: `device_${hash}`
  };
};

// Get WebGL vendor and renderer
const getWebGLVendor = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
      }
    }
  } catch (e) {
    // WebGL not available
  }
  return 'unknown';
};

const getWebGLRenderer = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      }
    }
  } catch (e) {
    // WebGL not available
  }
  return 'unknown';
};

// Simple hash function
const simpleHash = (str) => {
  let hash = 0;
  if (str.length === 0) return hash;
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(36);
};

// Store device fingerprint in localStorage
export const storeDeviceFingerprint = () => {
  const deviceData = generateDeviceFingerprint();
  localStorage.setItem('deviceFingerprint', JSON.stringify(deviceData));
  return deviceData;
};

// Get stored device fingerprint
export const getStoredDeviceFingerprint = () => {
  const stored = localStorage.getItem('deviceFingerprint');
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error('Error parsing stored device fingerprint:', e);
    }
  }
  return null;
};

// Check if current device matches stored fingerprint
export const isSameDevice = () => {
  const stored = getStoredDeviceFingerprint();
  if (!stored) return false;
  
  const current = generateDeviceFingerprint();
  
  // Compare key properties to determine if it's the same device
  const keyProperties = [
    'screenWidth', 'screenHeight', 'screenColorDepth',
    'platform', 'hardwareConcurrency', 'deviceMemory',
    'webglVendor', 'webglRenderer'
  ];
  
  let matchCount = 0;
  keyProperties.forEach(prop => {
    if (stored.fingerprint[prop] === current.fingerprint[prop]) {
      matchCount++;
    }
  });
  
  // Consider it the same device if 80% of key properties match
  const matchPercentage = (matchCount / keyProperties.length) * 100;
  return matchPercentage >= 80;
};

// Get device info for API calls
export const getDeviceInfo = () => {
  const deviceData = getStoredDeviceFingerprint() || generateDeviceFingerprint();
  return {
    deviceId: deviceData.deviceId,
    userAgent: navigator.userAgent,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    platform: navigator.platform,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
};
